import { useState } from 'react';
import type { BottleDTO, CellarDTO, ShelfDTO } from '@adega/shared';
import { nomeCompleto } from '@adega/shared';
import { useDuplicateBottle } from '../api/queries.js';
import { CellarGrid } from './CellarGrid.js';
import { ErrorBox } from './Feedback.js';

/**
 * Adiciona garrafas iguais a uma já cadastrada — a caixa com 6 do mesmo vinho.
 *
 * A pessoa marca várias vagas livres de uma vez (e/ou uma quantidade "sem
 * posição"); cada uma vira uma garrafa. Fotos e análise da IA são reaproveitadas
 * pelo backend: nenhum upload, nenhuma chamada de IA.
 */
export function AdicionarIguais({
  bottle,
  cellar,
  onConcluir,
}: {
  bottle: BottleDTO;
  cellar: CellarDTO | null | undefined;
  onConcluir: (quantidade: number) => void;
}) {
  const duplicar = useDuplicateBottle(bottle.id);
  const [vagas, setVagas] = useState<Array<{ shelfId: number; position: number; rotulo: string }>>([]);
  const [semPosicao, setSemPosicao] = useState(0);

  const total = vagas.length + semPosicao;

  const alternarVaga = (shelf: ShelfDTO, position: number) => {
    setVagas((atuais) => {
      const jaMarcada = atuais.some((v) => v.shelfId === shelf.id && v.position === position);
      return jaMarcada
        ? atuais.filter((v) => !(v.shelfId === shelf.id && v.position === position))
        : [...atuais, { shelfId: shelf.id, position, rotulo: `${shelf.label}, posição ${position + 1}` }];
    });
  };

  const confirmar = async () => {
    await duplicar.mutateAsync([
      ...vagas.map(({ shelfId, position }) => ({ shelfId, position })),
      ...Array.from({ length: semPosicao }, () => null),
    ]);
    onConcluir(total);
  };

  return (
    <div className="card space-y-3">
      <div>
        <p className="font-semibold">Adicionar garrafas iguais</p>
        <p className="text-sm text-adega-muted">
          Mais {nomeCompleto(bottle)}
          {bottle.vintage ? ` ${bottle.vintage}` : ''}? Toque nas posições livres onde elas vão
          ficar. Fotos e análise da IA são reaproveitadas — sem novo upload e sem custo de IA.
        </p>
      </div>

      {cellar ? (
        <CellarGrid cellar={cellar} onSelecionarVaga={alternarVaga} vagasSelecionadas={vagas} />
      ) : (
        <p className="text-sm text-adega-muted">
          Esta garrafa não está em nenhuma adega, então as iguais só podem ficar sem posição.
        </p>
      )}

      <div className="flex items-center justify-between gap-3 rounded-xl bg-adega-surface-2 px-3 py-2">
        <span className="text-sm">Sem posição por enquanto</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Diminuir garrafas sem posição"
            className="btn-secondary h-11 w-11 px-0"
            disabled={semPosicao === 0}
            onClick={() => setSemPosicao((n) => Math.max(0, n - 1))}
          >
            −
          </button>
          <span className="w-6 text-center font-semibold" aria-live="polite">
            {semPosicao}
          </span>
          <button
            type="button"
            aria-label="Aumentar garrafas sem posição"
            className="btn-secondary h-11 w-11 px-0"
            disabled={total >= 48}
            onClick={() => setSemPosicao((n) => n + 1)}
          >
            +
          </button>
        </div>
      </div>

      {vagas.length > 0 ? (
        <p className="text-xs text-adega-muted">
          Posições marcadas: {vagas.map((v) => v.rotulo).join(' · ')}
        </p>
      ) : null}

      {duplicar.error ? <ErrorBox erro={duplicar.error} /> : null}

      <button
        type="button"
        className="btn-primary w-full py-3"
        disabled={total === 0 || duplicar.isPending}
        onClick={() => void confirmar()}
      >
        {duplicar.isPending
          ? 'Adicionando…'
          : total === 0
            ? 'Marque ao menos uma posição'
            : `Adicionar ${total} ${total === 1 ? 'garrafa igual' : 'garrafas iguais'}`}
      </button>
    </div>
  );
}
